import { useEffect, useState } from "react";
import { CITIES_BY_PROVINCE, PROVINCES } from "../utils/constants";
import {
  formatPhoneNumber,
  getPhoneNumberError,
} from "../../../utils/common";
import { showToast } from "../../../utils/toast";

const INITIAL_FORM = {
  seller_id: "",
  full_name: "",
  phone: "",
  email: "",
  billing_address_line1: "",
  billing_address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "South Africa",
  currency: "ZAR",
};

export const useCheckoutForm = ({ user, profileData, excludeDelivery }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (e) => {
    let value = e.target.value;

    if (field === "phone") {
      value = value.replace(/[^0-9+() ]/g, "");
      value = formatPhoneNumber(value);
    }

    setForm((prev) => {
      const updated = { ...prev, [field]: value };

      // CHANGING THE PROVINCE CLEARS A CITY THAT NO LONGER FITS IT -- and only
      // then. This used to reset `city` to the province's FIRST city every
      // time, which quietly replaced the buyer's real city with whichever one
      // sorts first, and that is the address the order is placed against.
      //
      // It is also what made the delivery-address card flash.
      // `handleSavedAddressSelect` fills the form in field order -- line 1,
      // line 2, city, then state -- so the state write landed last and undid
      // the city: a Bellville address became Cape Town. `BillingForm` then
      // compared the form against the address it had just applied, saw `city`
      // disagree, took that for a manual edit and dropped the selection;
      // `SavedAddressSelector` saw nothing selected and re-applied the
      // default, which set the city again. The two effects drove each other
      // indefinitely and the row repainted on every pass.
      //
      // Reading `prev.city` is what makes this work inside that sequence:
      // functional updates queue in order, so the city set a moment earlier is
      // already present here.
      if (field === "state") {
        const province = PROVINCES.find(
          (p) => p.toLowerCase() === value.toLowerCase()
        );
        const cities = (province && CITIES_BY_PROVINCE[province]) || [];
        const currentCity = String(prev.city || "");
        const stillValid = cities.some(
          (c) => c.toLowerCase() === currentCity.toLowerCase()
        );

        if (stillValid) {
          updated.city = prev.city;
        } else if (cities.length > 0) {
          updated.city = cities[0];
        } else {
          updated.city = "";
        }
      }

      return updated;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.full_name.trim()) {
      newErrors.full_name = "Full name is required.";
    }

    const phoneError = getPhoneNumberError(form.phone);
    if (phoneError) {
      newErrors.phone = phoneError.endsWith(".")
        ? phoneError
        : `${phoneError}.`;
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Enter a valid email address.";
    }

    if (!excludeDelivery) {
      if (!form.billing_address_line1.trim()) {
        newErrors.billing_address_line1 = "Street address is required. Please select a saved address or enter a valid address.";
      }

      if (!form.state.trim()) {
        newErrors.state = "Please select a province.";
      } else {
        const isProvinceAllowed = PROVINCES.some(
          (p) => p.toLowerCase() === form.state.trim().toLowerCase()
        );
        if (!isProvinceAllowed) {
          newErrors.state = "Select a valid South Africa province.";
        }
      }

      if (!form.city.trim()) {
        newErrors.city = "Please select a city.";
      } else {
        const normalizedProvince = PROVINCES.find(
          (p) => p.toLowerCase() === form.state.trim().toLowerCase()
        );
        const allowedCities = normalizedProvince
          ? CITIES_BY_PROVINCE[normalizedProvince] || []
          : [];
        const isCityAllowed = allowedCities.some(
          (c) => c.toLowerCase() === form.city.trim().toLowerCase()
        );
        if (!isCityAllowed && allowedCities.length > 0) {
          newErrors.city = "Select a valid city in the chosen province.";
        }
      }

      if (!form.postal_code.trim()) {
        newErrors.postal_code = "Postal code is required.";
      } else if (!/^[A-Za-z0-9\-\s]{3,10}$/.test(form.postal_code)) {
        newErrors.postal_code = "Enter a valid postal code.";
      }

      if (!form.country.trim()) {
        newErrors.country = "Country is required.";
      }

      const hasValidAddress =
        form.billing_address_line1.trim() &&
        form.state.trim() &&
        form.city.trim() &&
        form.postal_code.trim() &&
        form.country.trim();

      if (!hasValidAddress) {
        showToast.error("Please select a saved address or enter a complete valid address using Google Places.");
        return false;
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length) {
      /* NO TOAST. `setErrors` has just put a message under every field that
         needs one, and a toast saying "Please fill in all required fields"
         adds a second, vaguer copy of what the form is already showing —
         which was the "Phone number is required." / toast pair on checkout.
         The address branch above keeps its toast: that failure has no field
         of its own to land in. */
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        full_name: user.full_name || prev.full_name,
        phone: user.phone_number || user.phone || prev.phone,
        email: user.email || prev.email,
      }));
    }
  }, [user]);

  useEffect(() => {
    if (profileData) {
      setForm((prev) => ({
        ...prev,
        full_name: profileData.full_name || prev.full_name,
        phone: profileData.phone_number || profileData.phone || prev.phone,
        email: profileData.email || prev.email,
      }));
    }
  }, [profileData]);

  const hasValidAddress = excludeDelivery || Boolean(
    form.billing_address_line1.trim() &&
    form.state.trim() &&
    form.city.trim() &&
    form.postal_code.trim() &&
    form.country.trim()
  );

  return { form, errors, handleChange, validateForm, hasValidAddress };
};
